import Collapse from "@material-ui/core/Collapse"
import List from "@material-ui/core/List"
import IconButton from "@material-ui/core/IconButton"
import Tooltip from "@material-ui/core/Tooltip"
import ListItem from "@material-ui/core/ListItem"
import ListItemIcon from "@material-ui/core/ListItemIcon"
import ListItemText from "@material-ui/core/ListItemText"
import Badge from "@material-ui/core/Badge"
import { Link, useLocation } from "react-router-dom"
import { useStyles } from "../hooks/use-styles"
import { type NavItem as NavItemType } from "../hooks/use-nav-items"
import clsx from "clsx"
import { ChevronDown, ChevronUp } from "react-feather"

const NavItem = ({
  item,
  tooltip,
  divider,
  onClick,
  badgeContent,
  isNested = false,
  isOpen = false,
}: {
  item: NavItemType
  onClick: (item: NavItemType) => void
  badgeContent?: string
  tooltip?: string
  divider?: boolean
  isNested?: boolean
  isOpen?: boolean
}) => {
  const classes = useStyles()
  const location = useLocation()

  const isActive = (path: string) => {
    const url = new URL(path, window.location.origin)
    return location.pathname === url.pathname
  }

  if ("items" in item) {
    const itemsCount = item.items.length
    return (
      <>
        <Tooltip title={tooltip || ""} placement="right">
          <ListItem
            button
            onClick={() => onClick(item)}
            className={classes.navItem}
            data-test={`resources-reveal-button`}
            divider={divider}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
            <IconButton size="small" className={"chevronIcon"}>
              {isOpen ? <ChevronUp /> : <ChevronDown />}
            </IconButton>
          </ListItem>
        </Tooltip>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.items.map((item, index) => {
              return (
                <NavItem
                  key={item.id}
                  item={item}
                  onClick={onClick}
                  badgeContent={"1"}
                  tooltip={item.label}
                  divider={index < itemsCount - 1}
                  isNested
                />
              )
            })}
          </List>
        </Collapse>
      </>
    )
  }

  const content = (
    <>
      {item.icon && (
        <ListItemIcon>
          <Badge overlap="rectangular" badgeContent={badgeContent || undefined} color="primary">
            {item.icon}
          </Badge>
        </ListItemIcon>
      )}
      <Tooltip title={item.label.length > 12 ? item.label : ""}>
        <ListItemText primary={item.label} />
      </Tooltip>
    </>
  )

  const className = clsx(classes.navItem, {
    [classes.nested]: isNested,
  })

  const commonProps = {
    button: true as const,
    className,
    divider,
    "data-test": `${item.label.toLowerCase()}-nav-link`,
    onClick: () => onClick(item),
  }

  return (
    <Tooltip key={item.path} title={tooltip || ""} placement="right">
      {item.external ? (
        <ListItem component="a" href={item.path} target="_blank" rel="noopener noreferrer" {...commonProps}>
          {content}
        </ListItem>
      ) : (
        <ListItem component={Link} to={item.path} selected={isActive(item.path)} {...commonProps}>
          {content}
        </ListItem>
      )}
    </Tooltip>
  )
}

export default NavItem
